import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SUPPORT_EMAIL, COMPANY_NAME, DBA_NAME, WEBSITE_URL } from '../constants/contact'

// NOTE: This is template legal text drafted as a structural starting point for a US LLC operating
// a subscription-based consumer web app. It is NOT legal advice and MUST be reviewed by a
// US-licensed attorney before publication. ETARIP LLC is organized under California law; the
// governing-law, arbitration, and venue clauses reflect that (Riverside County is the county of
// the LLC's address — adjust if the LLC's principal office moves).

export default function Terms() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-paybyphone-light">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/'} className="text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors">
            {DBA_NAME}
          </Link>
          {user && (
            <Link to="/dashboard" className="text-paybyphone-green hover:text-paybyphone-green/80">
              ← Back to Dashboard
            </Link>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> May 19, 2026<br />
              <strong>Last Updated:</strong> May 19, 2026
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                These Terms of Service (&quot;Terms&quot;) govern your access to and use of the website {WEBSITE_URL},
                including any associated subdomains, applications, and services (collectively, the &quot;Service&quot;)
                operated by {COMPANY_NAME}, a California limited liability company, doing business as {DBA_NAME}
                (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;).
              </p>
              <p className="text-gray-700 mb-4">
                By accessing, registering for, or using the Service, you agree to be bound by these Terms and
                our <Link to="/privacy" className="text-paybyphone-green underline">Privacy Policy</Link> and{' '}
                <Link to="/refund" className="text-paybyphone-green underline">Refund Policy</Link>, which are
                incorporated by reference. If you do not agree, do not use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                The Service is a digital tool that helps users locate available on-street and off-street parking
                spots using map data and aggregated information. The Service provides information and navigation
                assistance only; it does <strong>not</strong> reserve, guarantee, or pay for parking spaces, and
                does not collect or remit parking meter fees, parking tickets, or fines, which remain the sole
                responsibility of the user.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Eligibility and Account Registration</h2>
              <p className="text-gray-700 mb-4">
                You must be at least 18 years old and capable of forming a binding contract under U.S. law to use
                the Service. You agree to provide accurate, current, and complete information during registration
                and to keep this information updated. You are responsible for maintaining the confidentiality of
                your account credentials and for all activity that occurs under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Subscription Plans and Billing</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Plans</h3>
              <p className="text-gray-700 mb-4">
                The Service is offered on a paid subscription basis (weekly or monthly) and a pay-as-you-go option
                for time-limited parking sessions. All prices are displayed and charged in U.S. dollars (USD) and
                are exclusive of any applicable taxes, which are your responsibility.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Recurring Charges; Automatic Renewal</h3>
              <p className="text-gray-700 mb-4">
                Subscription plans renew automatically at the end of each billing period (weekly or monthly) at
                the then-current rate, until you cancel. By subscribing, you authorize us and our payment
                processor to charge your designated payment method on each renewal date. If a renewal payment
                fails, we may suspend your access to paid features and reattempt the charge in accordance with
                our payment-processor&apos;s rules.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Cancellation</h3>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time from your account settings or by contacting{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a>.
                Cancellation takes effect at the end of the current billing period; you will retain access until
                that date. No partial refund will be issued for the unused portion of a billing period, except as
                expressly provided in our <Link to="/refund" className="text-paybyphone-green underline">Refund Policy</Link>.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.4 Price Changes</h3>
              <p className="text-gray-700 mb-4">
                We may modify subscription prices from time to time. Material price changes will be communicated
                via email or in-app notice at least <strong>30 days</strong> in advance and will apply to the
                next billing cycle. If you do not agree to a price change, you may cancel your subscription
                before the change takes effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Payment Processing</h2>
              <p className="text-gray-700 mb-4">
                Payments are processed by a third-party payment processor. We do not store full payment-card
                numbers on our servers; only payment tokens and the last four digits required for billing support
                are retained. By providing your payment information, you authorize us and the payment processor
                to charge the payment method for amounts due under these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Refunds</h2>
              <p className="text-gray-700 mb-4">
                Refunds are governed by our <Link to="/refund" className="text-paybyphone-green underline">Refund Policy</Link>,
                which is incorporated into these Terms by reference. Please review the Refund Policy for full
                details on eligibility, request procedures, and timing.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Use the Service for any unlawful purpose or in violation of any applicable U.S. federal, state, or local law.</li>
                <li>Attempt to gain unauthorized access to the Service, accounts, or related systems.</li>
                <li>Interfere with or disrupt the integrity or performance of the Service.</li>
                <li>Scrape, reverse engineer, or copy the Service or its underlying data without our written consent.</li>
                <li>Resell, sublicense, or commercially exploit the Service without our written authorization.</li>
                <li>Share your account credentials with third parties.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate accounts that violate these provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                The Service, including all software, content, trademarks, logos, and graphics, is owned by or
                licensed to {COMPANY_NAME} and is protected by U.S. and international intellectual-property laws.
                You are granted a limited, non-exclusive, non-transferable, revocable license to use the Service
                for your personal, non-commercial purposes, subject to these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimers</h2>
              <p className="text-gray-700 mb-4">
                THE SERVICE IS PROVIDED ON AN <strong>&quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;</strong> BASIS, WITHOUT
                WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
                THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY PARKING SPOT INDICATED BY
                THE SERVICE WILL BE AVAILABLE, LEGAL, OR FREE OF FEES. PARKING REGULATIONS, FEES, AND PENALTIES
                ARE THE EXCLUSIVE RESPONSIBILITY OF THE USER.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL {COMPANY_NAME.toUpperCase()}, ITS
                MEMBERS, OFFICERS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, OR
                GOODWILL, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF
                THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATING TO
                THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO US IN
                THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (US $100).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to indemnify, defend, and hold harmless {COMPANY_NAME} and its members, officers,
                employees, and agents from and against any and all claims, liabilities, damages, losses, and
                expenses (including reasonable attorneys&apos; fees) arising out of or relating to (a) your use of
                or access to the Service; (b) your violation of these Terms; or (c) your violation of any
                third-party right, including any intellectual-property right or privacy right.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Dispute Resolution; Binding Arbitration</h2>
              <p className="text-gray-700 mb-4">
                <strong>Please read this section carefully — it affects your legal rights.</strong>
              </p>
              <p className="text-gray-700 mb-4">
                Any dispute, claim, or controversy arising out of or relating to these Terms or the Service will
                be resolved by <strong>binding individual arbitration</strong> administered by the American
                Arbitration Association (AAA) under its Consumer Arbitration Rules, in the English language, in
                Riverside County, California (or, at the consumer&apos;s election, via telephonic or video
                conference). Judgment on the arbitral award may be entered in any court of competent
                jurisdiction. Nothing in this section limits a consumer&apos;s right to seek public injunctive
                relief to the extent such waiver is prohibited by California law.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Class Action Waiver.</strong> You and {COMPANY_NAME} agree that disputes will be brought
                only in an individual capacity, and not as a plaintiff or class member in any purported class,
                collective, or representative action.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Opt-Out.</strong> You may opt out of arbitration within thirty (30) days of first
                accepting these Terms by sending written notice to{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a>,
                including your name, account email, and a clear statement that you opt out of arbitration.
              </p>
              <p className="text-gray-700 mb-4">
                Notwithstanding the foregoing, either party may bring an individual claim in small-claims court
                if it qualifies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of the State of California, United States, without regard
                to its conflict-of-laws principles. Subject to Section 12, the state and federal courts located
                in Riverside County, California, will have exclusive jurisdiction over any dispute not subject
                to arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may suspend or terminate your access to the Service at any time, with or without notice, if
                we believe you have violated these Terms or applicable law. Upon termination, your right to use
                the Service will cease immediately, but sections that by their nature should survive (including
                Sections 8–13 and 15) will survive.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Changes to These Terms</h2>
              <p className="text-gray-700 mb-4">
                We may modify these Terms from time to time. Material changes will be posted on this page with
                an updated &quot;Last Updated&quot; date, and we will provide reasonable notice (e.g., via email or
                in-app notification) before they take effect. Your continued use of the Service after the
                effective date constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Miscellaneous</h2>
              <p className="text-gray-700 mb-4">
                These Terms, together with the <Link to="/privacy" className="text-paybyphone-green underline">Privacy Policy</Link> and{' '}
                <Link to="/refund" className="text-paybyphone-green underline">Refund Policy</Link>, constitute the entire
                agreement between you and {COMPANY_NAME} concerning the Service. If any provision is held
                unenforceable, the remaining provisions will remain in full force. Our failure to enforce any
                right is not a waiver. You may not assign these Terms; we may assign them in connection with a
                merger, acquisition, or sale of assets.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact</h2>
              <p className="text-gray-700 mb-4">
                Questions about these Terms? See our{' '}
                <Link to="/contact" className="text-paybyphone-green underline">Contact page</Link> or email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-paybyphone-green underline">{SUPPORT_EMAIL}</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
